import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import Employees from './pages/Employees';
import Vehicles from './pages/Vehicles';
import Reports from './pages/Reports';
import Finance from './pages/Finance';
import Admin from './pages/Admin';
import ForgotPasswordEmail from './pages/ForgotPasswordEmail';
import ResetPasswordEmail from './pages/ResetPasswordEmail';

// Components
import SplashScreen from './components/SplashScreen';

// Styles
import './App.css';

// İnaktivite süresi: 2 saat
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

// Protected Route - Sadece giriş kontrolü
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Permission Route - İzin kontrolü
function PermissionRoute({ children, permission, fallbackMessage = "Bu sayfaya erişim yetkiniz yok." }) {
  const { isAuthenticated, hasPermission, loading } = useAuth();
  const navigate = useNavigate();
  
  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasPermission(permission)) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <span className="access-denied-icon">🚫</span>
          <h2>Erişim Engellendi</h2>
          <p>{fallbackMessage}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }
  
  return children;
}

// Admin Route - Sadece admin
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  
  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <span className="access-denied-icon">👑</span>
          <h2>Admin Yetkisi Gerekli</h2>
          <p>Bu sayfaya sadece yöneticiler erişebilir.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }
  
  return children;
}

// İnaktivite Takip Bileşeni
function InactivityTracker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  
  const handleInactivityLogout = useCallback(() => {
    if (location.pathname === '/login') return;
    
    logout();
    
    toast.error('2 saat boyunca işlem yapılmadığı için oturumunuz sonlandırıldı.', {
      duration: 5000,
      style: {
        background: '#ff9800',
        color: '#fff',
        fontWeight: '500',
      },
    });
    
    navigate('/login', { replace: true });
  }, [navigate, location.pathname, logout]);

  const updateActivity = useCallback(() => {
    if (isAuthenticated) {
      localStorage.setItem('lastActivity', Date.now().toString());
    }
  }, [isAuthenticated]);

  const checkInactivity = useCallback(() => {
    if (location.pathname === '/login') return;
    if (!isAuthenticated) return;
    
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) {
      updateActivity();
      return;
    }
    
    const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
    
    if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
      handleInactivityLogout();
    }
  }, [handleInactivityLogout, updateActivity, location.pathname, isAuthenticated]);

  useEffect(() => {
    updateActivity();
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => updateActivity();
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    const intervalId = setInterval(checkInactivity, 60000);
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkInactivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateActivity, checkInactivity]);

  return null;
}

// Ana Uygulama Routes
function AppRoutes() {
  return (
    <>
      <InactivityTracker />
      
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        
        {/* Dashboard - view_dashboard izni gerekli */}
        <Route path="/dashboard" element={
          <PermissionRoute permission="view_dashboard">
            <Dashboard />
          </PermissionRoute>
        } />
        
        {/* Siparişler - view_orders izni gerekli */}
        <Route path="/orders" element={
          <PermissionRoute permission="view_orders">
            <Orders />
          </PermissionRoute>
        } />
        
        {/* Müşteriler - view_customers izni gerekli */}
        <Route path="/customers" element={
          <PermissionRoute permission="view_customers">
            <Customers />
          </PermissionRoute>
        } />
        
        {/* Çalışanlar - view_employees izni gerekli */}
        <Route path="/employees" element={
          <PermissionRoute permission="view_employees">
            <Employees />
          </PermissionRoute>
        } />
        
        {/* Araçlar - view_vehicles izni gerekli */}
        <Route path="/vehicles" element={
          <PermissionRoute permission="view_vehicles">
            <Vehicles />
          </PermissionRoute>
        } />
        
        {/* Raporlar - view_reports izni gerekli */}
        <Route path="/reports" element={
          <PermissionRoute permission="view_reports">
            <Reports />
          </PermissionRoute>
        } />
        
        {/* Finans - view_finance izni gerekli */}
        <Route path="/finance" element={
          <PermissionRoute permission="view_finance">
            <Finance />
          </PermissionRoute>
        } />
        
        {/* Admin - Sadece admin */}
        <Route path="/admin" element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } />
        
        <Route path="/forgot-password-email" element={<ForgotPasswordEmail />} />
        <Route path="/reset-password-email" element={<ResetPasswordEmail />} />
        
        {/* Default */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    if (hasSeenSplash) {
      setShowSplash(false);
      setIsFirstLoad(false);
    }
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
    sessionStorage.setItem('hasSeenSplash', 'true');
  };

  if (showSplash && isFirstLoad) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Toaster position="top-center" />
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;